import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef } from 'react';
import useWidgetContext from '../../react/useWidgetContext';
import errorHandler from '../../service/errorHandler';
import { MY_ACCOUNT_CONFIG } from '../../constants/myAccount';
import DEFAULT_WIDGET_CONFIG from '../../constants/defaultWidgetConfig';
import CONSTANTS from '../../constants/constants';
import { assetManager } from '../../../../../common/service/assetManager';

interface IMyAccountWidget {
  setLoadingStatus: (status: boolean) => void;
  closeModal: () => void;
  onAuth: () => void;
  isTitleDisplayed?: boolean;
}

const MyAccountWidget: FunctionComponent<IMyAccountWidget> = ({
  setLoadingStatus,
  closeModal,
  onAuth,
  isTitleDisplayed = true,
}) => {
  const widgetId = useMemo(() => 'my_account_widget' + Math.floor(Math.random() * 100), []);
  const widgetInstance: any = useRef(null);

  const myAccountLibrary: any = useRef(null);
  const { configuration } = useWidgetContext();

  const ensureLibraryLoaded = () => {
    const widgetUrl: string = MY_ACCOUNT_CONFIG.widgetUrl;

    if (assetManager.isScriptLoaded(widgetUrl)) {
      if (!myAccountLibrary.current && (window as any).InchCapeWidgets) {
        myAccountLibrary.current = (window as any).InchCapeWidgets;
      }

      return Promise.resolve();
    }

    return Promise.resolve()
      .then(() => {
        if (!assetManager.isScriptLoaded(CONSTANTS.REACT_DEPENDENCIES.react)) {
          return assetManager
            .loadScript(CONSTANTS.REACT_DEPENDENCIES.react)
            .then(() => assetManager.loadScript(CONSTANTS.REACT_DEPENDENCIES.reactDom));
        }
      })
      .then(() => assetManager.loadScript(widgetUrl))
      .then(() => {
        myAccountLibrary.current = (window as any).InchCapeWidgets;
      });
  };

  const getLibrary = useCallback(() => {
    if (!myAccountLibrary.current) {
      throw new Error(`Attempted to use MyAccount library without loading it first`);
    }

    return myAccountLibrary.current;
  }, [myAccountLibrary.current]);

  const initializeWidget = () => {
    if (widgetInstance.current) {
      widgetInstance.current.destroy();
    }

    setLoadingStatus(true);

    const widgetConfig: any = {
      selector: '#' + widgetId,
      widget: 'authentication',
      language: DEFAULT_WIDGET_CONFIG.language,
      browserRouter: false,
      isTitleDisplayed,
      events: {
        is_logged: (data: any) => {
          setLoadingStatus(false);

          if (data.isLogged) {
            onAuth();
          }
        },
        login: onAuth,
        register: onAuth,
        finish_forgot_password: closeModal,
        error: (data: any) => {
          setLoadingStatus(false);
          errorHandler.handleError(data);
        },
      },
    };

    if (configuration.cookieDomain) {
      widgetConfig.cookieDomain = configuration.cookieDomain;
    }

    getLibrary().initialize(widgetConfig);
  };

  useEffect(() => {
    if (!widgetInstance.current) {
      ensureLibraryLoaded()
        .then(() => initializeWidget())
        .catch(errorHandler.promise());
    }

    return () => {
      if (widgetInstance.current) {
        widgetInstance.current.destroy();
      }
    };
  }, []);

  return <div id={widgetId} />;
};

export default MyAccountWidget;
